import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    Gift, Users, IndianRupee, CheckCircle2, Clock, Award, Search, ChevronLeft, ChevronRight, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const StatusPill = ({ status }) => {
    const map = {
        pending: "bg-amber-100 text-amber-700",
        qualified: "bg-emerald-100 text-emerald-700",
        expired: "bg-slate-100 text-slate-500",
    };
    return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${map[status] || "bg-slate-100 text-slate-500"}`}>
            {status || "—"}
        </span>
    );
};

const StatCard = ({ icon: Icon, label, value, hint, accent = "brand", testId }) => {
    const palette = {
        brand: "from-brand-600 to-brand-400",
        emerald: "from-emerald-600 to-emerald-400",
        amber: "from-amber-600 to-amber-400",
        rose: "from-rose-600 to-rose-400",
        slate: "from-slate-700 to-slate-500",
    };
    return (
        <div
            className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-soft transition-all hover:shadow-md"
            data-testid={testId}
        >
            <div className={`absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${palette[accent]} opacity-10`} />
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
                    <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 tabular-nums">{value}</p>
                    {hint && <p className="mt-1 line-clamp-1 text-xs text-slate-500">{hint}</p>}
                </div>
                <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${palette[accent]} text-white shadow-sm`}>
                    <Icon className="h-4 w-4" />
                </span>
            </div>
        </div>
    );
};

const ManualCreditModal = ({ referral, defaultAmount, onClose, onSuccess }) => {
    const [amount, setAmount] = useState(defaultAmount || 150);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const amt = parseFloat(amount);
        if (!(amt > 0) || amt > 10000) {
            toast.error("Amount must be between ₹1 and ₹10,000");
            return;
        }
        if (reason.trim().length < 4) {
            toast.error("Please provide a reason (min 4 characters)");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/referrals/${referral.id}/credit-manually`, {
                amount: amt,
                reason: reason.trim(),
            });
            toast.success(`Credited ₹${data.amount} to referrer wallet`);
            onSuccess?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Could not credit referrer");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 p-4 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-testid="manual-credit-modal">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600">Manual referral credit</p>
                <h2 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">Credit referrer manually</h2>
                <p className="mt-1 text-xs text-slate-500">
                    For: <strong className="text-slate-700">{referral.referrer_name}</strong> ({referral.referrer_email_masked || "—"})
                </p>
                <p className="text-xs text-slate-500">
                    Friend: <strong className="text-slate-700">{referral.referee_name}</strong>
                </p>

                <div className="mt-4 space-y-3">
                    <label className="block">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Amount (₹)</span>
                        <input
                            type="number"
                            min="1"
                            max="10000"
                            step="1"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="input-clean !rounded-lg mt-1"
                            data-testid="manual-credit-amount"
                        />
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Reason</span>
                        <textarea
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Customer support confirmed the qualifying order via call."
                            className="input-clean !rounded-lg mt-1"
                            data-testid="manual-credit-reason"
                        />
                    </label>
                </div>

                <div className="mt-5 flex gap-2">
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className="flex-1 rounded-lg border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50"
                        data-testid="manual-credit-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={busy}
                        className="flex-1 rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50"
                        data-testid="manual-credit-confirm"
                    >
                        {busy ? "Crediting…" : `Credit ₹${parseFloat(amount) || 0}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

const Content = () => {
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [selectedRef, setSelectedRef] = useState(null);

    const loadStats = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/referrals/stats");
            setStats(data);
        } catch {
            toast.error("Failed to load referral stats");
        }
    }, []);

    const loadList = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: pageSize };
            if (statusFilter) params.status = statusFilter;
            if (search.trim()) params.q = search.trim();
            const { data } = await api.get("/admin/referrals", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, statusFilter, search]);

    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => { loadList(); }, [loadList]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <div className="space-y-8" data-testid="admin-refer-earn">
            {/* Stats */}
            {!stats ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {[0, 1, 2, 3].map((i) => <div key={i} className="h-28 skeleton rounded-2xl" />)}
                </div>
            ) : (
                <>
                    <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <StatCard
                            icon={Users}
                            label="Invitations"
                            value={stats.totals.invitations.toLocaleString("en-IN")}
                            hint={`${stats.totals.active_referrers} active referrers`}
                            accent="brand"
                            testId="kpi-invitations"
                        />
                        <StatCard
                            icon={CheckCircle2}
                            label="Qualified"
                            value={stats.totals.qualified.toLocaleString("en-IN")}
                            hint={`${stats.totals.pending} pending`}
                            accent="emerald"
                            testId="kpi-qualified"
                        />
                        <StatCard
                            icon={IndianRupee}
                            label="Disbursed"
                            value={inr(stats.totals.total_disbursed_inr)}
                            hint={`Across ${stats.totals.referrer_credited + stats.totals.referee_credited} credits`}
                            accent="amber"
                            testId="kpi-disbursed"
                        />
                        <StatCard
                            icon={Gift}
                            label="Reward bands"
                            value={`${inr(stats.rewards.reward_referee)}/${inr(stats.rewards.reward_referrer)}`}
                            hint={`Qualify ≥ ${inr(stats.rewards.qualify_min_order)} order`}
                            accent="slate"
                            testId="kpi-reward-bands"
                        />
                    </section>

                    {/* Top referrers */}
                    {stats.top_referrers.length > 0 && (
                        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="top-referrers-card">
                            <header className="mb-4 flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Top 5 referrers</p>
                                    <h3 className="text-sm font-semibold text-slate-900">Friend-bringing champions</h3>
                                </div>
                                <Award className="h-4 w-4 text-amber-500" />
                            </header>
                            <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                                {stats.top_referrers.map((r, i) => (
                                    <li key={r.user_id} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3" data-testid={`top-referrer-${i}`}>
                                        <p className="line-clamp-1 text-xs font-semibold text-slate-900">{r.name}</p>
                                        <p className="line-clamp-1 text-[11px] text-slate-500">{r.email_masked}</p>
                                        <div className="mt-2 flex items-center gap-2 text-[11px]">
                                            <span className="rounded bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-700">{r.qualified} qual</span>
                                            <span className="text-slate-500">/ {r.invited} inv</span>
                                        </div>
                                        {r.referral_code && <p className="mt-1 text-[10px] font-mono text-brand-700">{r.referral_code}</p>}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </>
            )}

            {/* Filters */}
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft" data-testid="referrals-filters">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 flex-1 min-w-[240px]">
                        <Search className="h-4 w-4 text-slate-400" />
                        <input
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            placeholder="Search by name, email, or code…"
                            className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
                            data-testid="referrals-search"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                        data-testid="referrals-status-filter"
                    >
                        <option value="">All statuses</option>
                        <option value="pending">Pending</option>
                        <option value="qualified">Qualified</option>
                    </select>
                </div>
            </section>

            {/* Table */}
            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft" data-testid="referrals-list">
                {loading ? (
                    <div className="space-y-3 p-5">
                        {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-14 skeleton rounded-lg" />)}
                    </div>
                ) : items.length === 0 ? (
                    <div className="px-5 py-12 text-center text-sm text-slate-400">
                        <AlertCircle className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                        No referrals match your filter.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="border-b border-slate-100 bg-slate-50/60 text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-4 py-2.5 text-left">Referrer</th>
                                    <th className="px-4 py-2.5 text-left">Friend</th>
                                    <th className="px-4 py-2.5 text-left">Status</th>
                                    <th className="px-4 py-2.5 text-left">Signed up</th>
                                    <th className="px-4 py-2.5 text-left">Credits</th>
                                    <th className="px-4 py-2.5 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((r) => (
                                    <tr key={r.id} className="hover:bg-slate-50/40" data-testid={`referral-row-${r.id}`}>
                                        <td className="px-4 py-3">
                                            <Link to={`/admin/users/${r.referrer_user_id}`} className="font-medium text-slate-900 hover:text-brand-700">
                                                {r.referrer_name}
                                            </Link>
                                            <p className="line-clamp-1 text-[11px] text-slate-500">{r.referrer_email_masked}</p>
                                            {r.referrer_code && <p className="text-[10px] font-mono text-brand-600">{r.referrer_code}</p>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="font-medium text-slate-900">{r.referee_name}</p>
                                            <p className="line-clamp-1 text-[11px] text-slate-500">{r.referee_email_masked}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusPill status={r.status} />
                                        </td>
                                        <td className="px-4 py-3 text-xs text-slate-600">
                                            {r.referee_signed_up_at ? new Date(r.referee_signed_up_at).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—"}
                                        </td>
                                        <td className="px-4 py-3 text-[11px]">
                                            <div className="flex flex-col gap-0.5">
                                                <span className={r.reward_referee_credited ? "text-emerald-700" : "text-slate-400"}>
                                                    {r.reward_referee_credited ? "✓" : "○"} Friend
                                                </span>
                                                <span className={r.reward_referrer_credited ? "text-emerald-700" : "text-slate-400"}>
                                                    {r.reward_referrer_credited ? "✓" : "○"} Referrer
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {r.reward_referrer_credited ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700">
                                                    <CheckCircle2 className="h-3 w-3" /> Credited
                                                </span>
                                            ) : (
                                                <button
                                                    onClick={() => setSelectedRef(r)}
                                                    className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-1.5 text-[11px] font-semibold text-brand-700 hover:bg-brand-100"
                                                    data-testid={`credit-manually-btn-${r.id}`}
                                                >
                                                    Credit manually
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {total > pageSize && (
                    <footer className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
                        <span>Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
                        <div className="flex items-center gap-1">
                            <button
                                disabled={page <= 1}
                                onClick={() => setPage((p) => p - 1)}
                                className="rounded-md border border-slate-200 p-1.5 text-slate-700 disabled:opacity-40 hover:bg-slate-50"
                                data-testid="referrals-pagination-prev"
                            >
                                <ChevronLeft className="h-3.5 w-3.5" />
                            </button>
                            <span className="px-2 font-medium text-slate-700">{page} / {totalPages}</span>
                            <button
                                disabled={page >= totalPages}
                                onClick={() => setPage((p) => p + 1)}
                                className="rounded-md border border-slate-200 p-1.5 text-slate-700 disabled:opacity-40 hover:bg-slate-50"
                                data-testid="referrals-pagination-next"
                            >
                                <ChevronRight className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </footer>
                )}
            </section>

            {selectedRef && (
                <ManualCreditModal
                    referral={selectedRef}
                    defaultAmount={stats?.rewards?.reward_referrer || 150}
                    onClose={() => setSelectedRef(null)}
                    onSuccess={() => {
                        setSelectedRef(null);
                        loadStats();
                        loadList();
                    }}
                />
            )}
        </div>
    );
};

const AdminReferEarn = () => (
    <AdminAuthGuard>
        <AdminLayout title="Refer & Earn" subtitle="Track referrals, top promoters & manual credits">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminReferEarn;
